import React from 'react';
import { Box } from '@mui/material';
import { Bar } from 'react-chartjs-2';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  Title, 
  Tooltip, 
  Legend 
} from 'chart.js';

// Register the required Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

/**
 * OutcomeByAutomationStatusChart component for displaying test outcomes by automation status
 * @param {Object} props - Component props
 * @param {Array} props.testExecutions - Array of test execution objects
 * @returns {JSX.Element} OutcomeByAutomationStatusChart component
 */
const OutcomeByAutomationStatusChart = ({ testExecutions = [] }) => {
  // Group tests by automation status and outcome
  const groupedData = testExecutions.reduce((acc, test) => {
    const automationStatus = test.automationStatus || 'Not Specified';
    const outcome = test.outcome || 'Not Run';
    
    if (!acc[automationStatus]) {
      acc[automationStatus] = {};
    }
    
    acc[automationStatus][outcome] = (acc[automationStatus][outcome] || 0) + 1;
    return acc;
  }, {});
  
  // Get unique automation statuses and outcomes
  const automationStatuses = Object.keys(groupedData);
  const allOutcomes = new Set();
  
  // Collect all unique outcomes
  automationStatuses.forEach(status => {
    Object.keys(groupedData[status]).forEach(outcome => {
      allOutcomes.add(outcome);
    });
  });
  
  const outcomes = Array.from(allOutcomes);
  
  // Define colors for each outcome
  const outcomeColors = {
    'Passed': '#4caf50', // Green
    'Failed': '#f44336', // Red
    'Not Run': '#9e9e9e', // Grey
    'Blocked': '#ff9800', // Orange
    'Not Applicable': '#2196f3', // Blue
    'Inconclusive': '#673ab7', // Purple
    'Not Executed': '#795548' // Brown
  };
  
  // Prepare datasets for each outcome
  const datasets = outcomes.map(outcome => {
    return {
      label: outcome,
      data: automationStatuses.map(status => groupedData[status][outcome] || 0),
      backgroundColor: outcomeColors[outcome] || '#9e9e9e',
      borderColor: 'transparent',
      borderWidth: 1
    };
  });
  
  // Chart data
  const chartData = {
    labels: automationStatuses,
    datasets: datasets
  };
  
  // Chart options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: {
          precision: 0 // Only show whole numbers
        }
      }
    },
    plugins: {
      title: {
        display: true,
        text: 'Test Outcomes by Automation Status',
        font: {
          size: 16
        }
      },
      legend: {
        position: 'top',
        labels: {
          boxWidth: 15,
          padding: 15
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.dataset.label || '';
            const value = context.raw || 0;
            return `${label}: ${value}`;
          }
        }
      }
    }
  };
  
  // If no data, display a message
  if (automationStatuses.length === 0) {
    return (
      <Box 
        display="flex" 
        alignItems="center" 
        justifyContent="center" 
        height="100%"
      >
        No automation status data available
      </Box>
    );
  }
  
  return (
    <Box height="300px" width="100%" overflow="hidden">
      <Bar data={chartData} options={options} />
    </Box>
  );
};

export default OutcomeByAutomationStatusChart;
